import { defineStore } from 'pinia'
import {
  changePassword,
  login,
  refreshToken,
  updateTemporaryPassword,
} from '../modules/auth/services/authentication'
import { Module } from '@/modules/payrolls-config/models'

export const useUserStore = defineStore('user', {
  persist: true,
  state: (): state => {
    return {
      user: null,
      company: null,
    }
  },
  getters: {
    async isAuthenticated(): Promise<boolean> {
      return Promise.resolve(!!this.user?.idToken)
    },
    async idToken(): Promise<string> {
      return Promise.resolve(this.user?.idToken || '')
    },
  },
  actions: {
    async login(email: string, password: string): Promise<boolean | Error> {
      const response = await login(email, password)
      if (response) {
        this.user = {
          email: response?.user?.email || '',
          id: response?.user?.id || '',
          name: response?.user?.name || '',
          roles: {
            id: response?.user?.role?.id,
            name: response?.user?.role?.name,
          },
          modules: response.modules || null,
          companies: response.user?.companies || [],
          employeeId: response?.user?.employeeId || '',
          clientId: response?.user?.clientId || '',
          idToken: response?.idToken || '',
          refreshToken: response?.refreshToken || '',
          accessToken: response?.accesToken || '',
          session: response.session,
          isRRHH: response.user?.isRRHH || false,
          approverId: response.user?.approverId || '',
          payrollConfigId: response.user?.payrollConfigId || '',
        }

        this.company = {
          id: response.company?.id,
          code: response.company?.code,
          name: response.company?.name,
          logoUrl: response.company?.logo,
        }
        return true
      }

      return false
    },
    async updateTemporaryPassword(email: string, newPassword: string) {
      const response = await updateTemporaryPassword(
        email,
        newPassword,
        this.user?.session || ''
      )

      if (response) {
        this.user = {
          email: response?.user?.email || '',
          id: response?.user?.id || '',
          name: response?.user?.name || '',
          roles: {
            id: response?.user?.role?.id,
            name: response?.user?.role?.name,
          },
          idToken: response?.idToken || '',
          refreshToken: response?.refreshToken || '',
          accessToken: response?.accesToken || '',
          session: response?.session,
          clientId: response?.user?.clientId || '',
          employeeId: response?.user?.employeeId || '',
          companies: response.user?.companies || [],
          isRRHH: response.user?.isRRHH || false,
          approverId: response.user?.approverId || '',
        }
        return true
      }

      return false
    },
    async updatePassword(oldPassword: string, newPassword: string) {
      try {
        const response = await changePassword(
          { newPassword, oldPassword },
          this.user?.accessToken || ''
        )

        return response
      } catch (err) {
        return false
      }
    },
    async logout() {
      this.user = null
    },
    async refreshToken(): Promise<string> {
      if (!this.user?.email || !this.user?.refreshToken) return ''

      const response = await refreshToken(
        this.user.email,
        this.user.refreshToken
      )

      if (!response) return ''

      this.user = {
        id: this.user?.id || '',
        name: this.user?.name || '',
        email: this.user?.email || '',
        roles: this.user?.roles || { id: '', name: '' },
        refreshToken: this.user?.refreshToken || '',
        idToken: response.idToken,
        accessToken: response.accesToken || '',
        session: response.session,
        modules: this.user?.modules,
        clientId: this.user?.clientId || '',
        employeeId: this.user?.employeeId || '',
        companies: this.user?.companies || [], // companies assigned to the user
        approverId: this.user?.approverId || '',
        isRRHH: this.user?.isRRHH || false,
      }
      return response.idToken
    },
  },
})

interface state {
  user: UserInfo | null
  company: CompanyInfo | null
}

interface UserInfo {
  id: string
  name: string
  email: string
  roles: Role
  idToken: string
  refreshToken: string
  accessToken: string
  session?: string
  modules?: Module[]
  employeeId?: string
  companies?: string[]
  clientId: string
  payrollConfigId?: string
  isRRHH?: boolean
  approverId?: string
  isBackup?: boolean
}

interface CompanyInfo {
  id: string
  code?: string
  name: string
  logoUrl?: string
}

interface Role {
  id: string
  name: string
}
